import { useTranslation } from '@app/hooks/use-translation';
import {
  StatisticWidgetFullInfoType,
  StatisticWidgetObjectFromBulk,
  StatisticWidgetsBulkRequestType,
} from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';
import { Box, CircleButton } from '@ui';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import { useLazyGetStatisticWidgetFullInfoQuery } from '@app/store/api/qm-module/qm-statistic/qm-statistic-page.api';

import { IconNameType } from '@app/components/ui/icons/icon-name.type';

import { StatisticOutletContextType } from '../type';

import WidgetWrapper from './widget-wrapper';
import { MonitoringScoreDiagramHalf } from './monitoring-score-diagram-half';
import { CriteriaWidgetBlock } from './criteria-widget-block';
import { MonitoringScoreDiagram } from './monitoring-score-diagram';

const MonitoringScoreDiagramHalfMemo = React.memo(MonitoringScoreDiagramHalf);
const CriteriaWidgetBlockMemo = React.memo(CriteriaWidgetBlock);
const MonitoringScoreDiagramMemo = React.memo(MonitoringScoreDiagram);

type StatisticWidgetBlockPropsType = {
  widgetData: StatisticWidgetObjectFromBulk;
  mform_id: string;
  widgetsList?: string[];
};

const tabIconsObject: Record<'monitoring' | 'operators' | 'teams', IconNameType> = {
  monitoring: 'TableIcon',
  operators: 'BeatsHeadphonesIcon',
  teams: 'Person3Icon',
};
const widgetsListFromStorageKey = 'WLMAP';
const StatisticWidgetBlock: FC<StatisticWidgetBlockPropsType> = (props) => {
  const { widgetData, mform_id, widgetsList } = props;
  const { t } = useTranslation('pages.qmManage.statistic.widget');
  const navigate = useNavigate();
  const { widgetsFilterBlock, navigationItems, setNavigationItems, teamsList } =
    useOutletContext<StatisticOutletContextType>();
  const firstRandomTeam = teamsList?.qm_teams[0]?.team_id;
  const [getWidgetFull] = useLazyGetStatisticWidgetFullInfoQuery();
  const [fullWidget, setFullWidget] = useState<StatisticWidgetFullInfoType>();
  const [widgetsFilter, setWidgetsFilter] = useState(widgetsFilterBlock);
  const [isRequesting, setIsRequesting] = useState(false);

  const handleOpenFullWidget = useCallback(
    (filter: Partial<StatisticWidgetsBulkRequestType>) => {
      if (isRequesting) return;
      setIsRequesting(true);
      setWidgetsFilter(filter);
      getWidgetFull({ body: filter, params: { mform_id } })
        .unwrap()
        .then((data) => {
          setFullWidget(data);
        })
        .finally(() => {
          setIsRequesting(false);
        });
    },
    [getWidgetFull, isRequesting, mform_id],
  );

  useEffect(() => {
    if (
      !fullWidget ||
      isRequesting ||
      JSON.stringify(widgetsFilter) === JSON.stringify(widgetsFilterBlock)
    )
      return;

    handleOpenFullWidget(widgetsFilterBlock);
  }, [fullWidget, handleOpenFullWidget, isRequesting, widgetsFilter, widgetsFilterBlock]);

  // WRAPPER
  const widgetsListFromStorage = localStorage.getItem(widgetsListFromStorageKey)
    ? JSON.parse(localStorage.getItem(widgetsListFromStorageKey) as string)
    : undefined;
  const [openedWidgets, setOpenedWidgets] = useState<string[]>(
    widgetsListFromStorage || widgetsList || [],
  );

  function handleClickWidgetAccordion(id: string) {
    setOpenedWidgets((prevItems) => {
      const index = prevItems.indexOf(id);
      if (index === -1) {
        const result = [...prevItems, id];
        localStorage.setItem(widgetsListFromStorageKey, JSON.stringify(result));
        return result;
      } else {
        setFullWidget(undefined);
        const result = prevItems.filter((item) => item !== id);
        localStorage.setItem(widgetsListFromStorageKey, JSON.stringify(result));
        return result;
      }
    });
  }

  function handleOpenTap(key: 'monitoring' | 'operators' | 'teams', name: string) {
    const link =
      key === 'operators'
        ? `./${key}/${mform_id}${firstRandomTeam ? '/' + firstRandomTeam : ''}`
        : `./${key}/${mform_id}`;
    const count = navigationItems.reduce((acc, cur) => {
      acc += Number(!!String(cur.index).includes(link));
      return acc;
    }, 0);
    const state = {
      ...widgetsFilterBlock,
      filter: widgetsFilterBlock.operators
        ? [{ field: 'operator', value: widgetsFilterBlock.operators, condition: '=' }]
        : [],
    };

    const params = encodeURIComponent(JSON.stringify(state));
    setNavigationItems((prev) => [
      ...prev,
      {
        index: `${count ? link + `/${count}` : link}?filter=${params}`,
        link: `${count ? link + `/${count}` : link}?filter=${params}`,
        label: !count ? name : `${name}(${count})`,
        icon: tabIconsObject[key],
        // state,
      },
    ]);
    navigate(`${count ? link + `/${count}` : link}?filter=${params}`);
  }

  // WRAPPER

  return (
    <WidgetWrapper
      handleClickWidgetAccordion={handleClickWidgetAccordion}
      handleOpenTap={handleOpenTap}
      openedWidgets={openedWidgets}
      widgetData={widgetData}
      fullWidget={fullWidget}
      setFullWidget={setFullWidget}
      handleOpenFullWidget={handleOpenFullWidget}
      setOpenedWidgets={setOpenedWidgets}
      widgetsFilterBlock={widgetsFilterBlock}
    >
      <div className="flex flex-col gap-[10px] h-full w-full">
        <div className="flex items-start gap-[10px] pt-2">
          {Object.entries(widgetData).map((widget, index) => {
            const [key, value] = widget;
            if (key === 'mfs' || key === 'score_max_calc' || key === 'assigned') return;
            return (
              <Box
                key={key + value + index}
                className="flex flex-col w-full justify-between p-2 gap-[10px] h-[150px]"
              >
                <div className="flex items-start justify-between">
                  <span className="text-[14px] text-2color line-clamp-2">{t(key)}</span>
                  <CircleButton
                    onClick={() => handleOpenFullWidget(widgetsFilterBlock)}
                    size={14}
                    className="text-action"
                    icon="HelpCircleIcon"
                  />
                </div>
                {key !== 'score_avg' && (
                  <div className="text-1color text-[42px] bold flex items-end">
                    <div>{String(value)}</div>
                    {key === 'completed' && (
                      <div className="self-start mt-[12px] text-[16px]">
                        &nbsp;/ {widgetData['assigned']} (
                        {widgetData['assigned'] &&
                          ((Number(value) / widgetData['assigned']) * 100).toFixed(0)}{' '}
                        % )
                      </div>
                    )}
                    {key === 'duration_avg' && (
                      <span className="text-[14px] self-end text-3color mb-[12px]">сек</span>
                    )}
                  </div>
                )}
                {key === 'score_avg' && (
                  <div className="w-full h-full flex items-center justify-center">
                    <MonitoringScoreDiagramHalfMemo
                      formatting={fullWidget?.conditional_formatting}
                      fontSize="22px"
                      maxScore={widgetData?.['score_max_calc'] || 0}
                      score={+value}
                      width={200}
                      height={200}
                    />
                  </div>
                )}
              </Box>
            );
          })}
          {fullWidget &&
            fullWidget.conditional_formatting?.map((condition, index) => (
              <Box
                key={condition.desc + condition.value + index}
                className="flex flex-col w-full justify-between p-2 gap-[10px] h-[150px] relative overflow-hidden"
              >
                <div
                  style={{ background: condition.color }}
                  className="absolute w-full h-[40px] self-center left-0 top-0 z-[0] "
                />
                <div className="flex items-start justify-between relative z-[10] ">
                  <span className="text-[14px] text-2color line-clamp-2 relative">
                    {condition.desc}
                  </span>
                  <CircleButton
                    onClick={() => handleOpenFullWidget(widgetsFilterBlock)}
                    size={14}
                    className="text-action"
                    icon="HelpCircleIcon"
                  />
                </div>
                <div className="text-1color text-[28px] bold ">
                  {condition.count}
                  {'('}
                  {condition.pct + ' %'}
                  {')'}
                </div>
              </Box>
            ))}
        </div>
        {fullWidget && (
          <Box className="p-6 h-full relative">
            <div className="w-full flex justify-end">
              <div className="w-[150px] text-center text-[15px] font-bold text-2color">
                {t('middle_score')}
              </div>
            </div>
            <div className="flex flex-col gap-[30px]">
              {fullWidget.criteria.blocks?.map((block) => (
                <div key={block.id} className="flex items-start ">
                  <div>
                    <div className="text-[15px] font-bold text-2color text-center">
                      {block.name}
                    </div>
                    <div>
                      <MonitoringScoreDiagramMemo
                        maxScore={block.score_max_calc || 0}
                        width={200}
                        fontSize="19px"
                        formatting={block.conditional_formatting}
                        height={150}
                        score={block.score_avg}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-[10px] w-full">
                    {block.criteria.map((criteria) => (
                      <CriteriaWidgetBlockMemo key={criteria.id} criteria={criteria} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-[30px] h-full flex items-center w-full">
              <div className="font-bold text-2color text-[15px] max-w-[200px] min-w-[200px] text-center">
                {t('no_block')}
              </div>
              <div className=" flex-col gap-[10px] w-full">
                {fullWidget.criteria.plain.map((criteria) => (
                  <CriteriaWidgetBlockMemo key={criteria.id} criteria={criteria} />
                ))}
              </div>
            </div>
          </Box>
        )}
      </div>
    </WidgetWrapper>
  );
};

export default StatisticWidgetBlock;
